import React, { useEffect, useState } from 'react';
import { PlusIcon, SearchIcon, EditIcon, TrashIcon, PhoneIcon, MailIcon, DownloadIcon, FileSpreadsheetIcon, ShieldIcon, ArchiveIcon, ArchiveRestoreIcon, PowerOffIcon, CheckCircleIcon, RotateCcwIcon, XCircleIcon, LockIcon } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Modal } from '../ui/Modal';
import { Badge } from '../ui/Badge';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { Insurance as InsuranceType } from '../../types';
import { exportInsurancesToExcel } from '../../utils/excelExport';
import { exportInsurancesToPdf } from '../../utils/pdfExport';
import { usePagination } from '../../hooks/usePagination';
import { useSortableData } from '../../hooks/useSortableData';
import { Pagination } from '../ui/Pagination';
import { SortableHeader } from '../ui/SortableHeader';
import { ExcelExportModal } from '../ui/ExcelExportModal';
import { PdfExportModal } from '../ui/PdfExportModal';
export function Insurance() {
  const {
    insurances,
    addInsurance,
    updateInsurance,
    deleteInsurance
  } = useData();
  const {
    user
  } = useAuth();
  const {
    canCreate,
    canEdit,
    canDelete,
    isReadOnly
  } = usePermissions();
  const readOnly = isReadOnly('insurance');
  const isAdmin = user?.role === 'administrador';
  const userRoleNorm = user?.role?.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') || '';
  const canExportExcel = isAdmin || user?.role === 'terapeuta' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  const canExportPdf = isAdmin || user?.role === 'terapeuta' || userRoleNorm === 'recepcion' || userRoleNorm === 'recepcionista';
  const [searchTerm, setSearchTerm] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedInsurance, setSelectedInsurance] = useState<InsuranceType | null>(null);
  const [isActionConfirmOpen, setIsActionConfirmOpen] = useState(false);
  const [actionType, setActionType] = useState<'archive' | 'deactivate' | 'delete' | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    coverageType: '',
    phone: '',
    email: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isExporting, setIsExporting] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const [isPdfExportModalOpen, setIsPdfExportModalOpen] = useState(false);
  // Archive/Deactivate Logic
  const STORAGE_KEY = 'savidna-insurance-status';
  const [itemStatuses, setItemStatuses] = useState<Record<string, 'archived' | 'deactivated'>>({});
  const [viewMode, setViewMode] = useState<'active' | 'archived' | 'deactivated'>('active');
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) setItemStatuses(JSON.parse(saved));
    } catch {}
  }, []);
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(itemStatuses));
  }, [itemStatuses]);
  const getItemStatus = (id: string): 'active' | 'archived' | 'deactivated' => itemStatuses[id] || 'active';
  const archiveItem = (id: string) => {
    setItemStatuses(prev => ({
      ...prev,
      [id]: 'archived'
    }));
    updateInsurance(id, {
      status: 'archived'
    } as any).catch(e => console.error('Error archiving insurance in Supabase:', e));
    setIsActionConfirmOpen(false);
  };
  const deactivateItem = (id: string) => {
    setItemStatuses(prev => ({
      ...prev,
      [id]: 'deactivated'
    }));
    updateInsurance(id, {
      status: 'deactivated'
    } as any).catch(e => console.error('Error deactivating insurance in Supabase:', e));
    setIsActionConfirmOpen(false);
  };
  const restoreItem = (id: string) => {
    setItemStatuses(prev => {
      const next = {
        ...prev
      };
      delete next[id];
      return next;
    });
    updateInsurance(id, {
      status: 'active'
    } as any).catch(e => console.error('Error restoring insurance in Supabase:', e));
    setIsActionConfirmOpen(false);
  };
  const filteredInsurances = insurances.filter(i => {
    const matchesSearch = i.name.toLowerCase().includes(searchTerm.toLowerCase()) || i.company.toLowerCase().includes(searchTerm.toLowerCase());
    const status = getItemStatus(i.id);
    return matchesSearch && status === viewMode;
  });
  const {
    sortedItems: sortedInsurances,
    requestSort,
    isSorted
  } = useSortableData(filteredInsurances);
  const {
    paginatedItems: paginatedInsurances,
    currentPage,
    totalPages,
    totalItems,
    startIndex,
    endIndex,
    goToPage,
    nextPage,
    prevPage
  } = usePagination(sortedInsurances);
  // Counts for tabs
  const activeCount = insurances.filter(i => getItemStatus(i.id) === 'active').length;
  const archivedCount = insurances.filter(i => getItemStatus(i.id) === 'archived').length;
  const deactivatedCount = insurances.filter(i => getItemStatus(i.id) === 'deactivated').length;
  const openNewForm = () => {
    if (!canCreate('insurance')) return;
    setSelectedInsurance(null);
    setFormData({
      name: '',
      company: '',
      coverageType: '',
      phone: '',
      email: ''
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openEditForm = (insurance: InsuranceType) => {
    if (!canEdit('insurance')) return;
    setSelectedInsurance(insurance);
    setFormData({
      name: insurance.name,
      company: insurance.company,
      coverageType: insurance.coverageType,
      phone: insurance.phone,
      email: insurance.email
    });
    setFormErrors({});
    setIsFormOpen(true);
  };
  const openActionConfirm = (insurance: InsuranceType, type: 'archive' | 'deactivate' | 'delete') => {
    setSelectedInsurance(insurance);
    setActionType(type);
    setIsActionConfirmOpen(true);
  };
  const handleAction = () => {
    if (!selectedInsurance || !actionType) return;
    if (actionType === 'archive') {
      archiveItem(selectedInsurance.id);
    } else if (actionType === 'deactivate') {
      deactivateItem(selectedInsurance.id);
    } else if (actionType === 'delete') {
      deleteInsurance(selectedInsurance.id);
      setIsActionConfirmOpen(false);
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!formData.name) errors.name = 'El nombre es requerido';
    if (!formData.company) errors.company = 'La compañía es requerida';
    if (!formData.coverageType) errors.coverageType = 'El tipo de cobertura es requerido';
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    if (selectedInsurance) {
      updateInsurance(selectedInsurance.id, formData);
    } else {
      addInsurance(formData);
    }
    setIsFormOpen(false);
  };
  const handleExportToExcel = async (dateFrom: string | null, dateTo: string | null) => {
    setIsExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    exportInsurancesToExcel(insurances);
    setIsExporting(false);
    setIsExportModalOpen(false);
  };
  const handleExportToPdf = async (dateFrom: string | null, dateTo: string | null) => {
    setIsPdfExporting(true);
    await new Promise(resolve => setTimeout(resolve, 500));
    exportInsurancesToPdf(insurances);
    setIsPdfExporting(false);
    setIsPdfExportModalOpen(false);
  };
  return <div className="space-y-6" data-id="element-2519">
      {/* Header Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-teal-600 via-teal-500 to-cyan-500 p-6 text-white shadow-xl" data-id="element-2520">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" data-id="element-2521" />
        <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4" data-id="element-2522">
          <div data-id="element-2523">
            <div className="flex items-center gap-3 mb-2" data-id="element-2524">
              <div className="p-2.5 bg-white/15 backdrop-blur-sm rounded-xl" data-id="element-2525">
                <ShieldIcon className="w-6 h-6" data-id="element-2526" />
              </div>
              <h1 className="text-2xl font-bold" data-id="element-2527">Seguros</h1>
            </div>
            <p className="text-teal-100 max-w-xl" data-id="element-2528">
              Gestión de seguros médicos y coberturas
              {readOnly && <span className="ml-2 bg-white/20 px-2 py-0.5 rounded text-xs font-medium" data-id="element-2529">
                  Solo lectura
                </span>}
            </p>
          </div>
          <div className="flex gap-3" data-id="element-2530">
            {canExportPdf && <Button variant="outline" leftIcon={<ShieldIcon className="w-4 h-4" data-id="element-2532" />} onClick={() => setIsPdfExportModalOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-2531">
                Exportar PDF
              </Button>}
            {canExportExcel && <Button variant="outline" leftIcon={<DownloadIcon className="w-4 h-4" data-id="element-2534" />} onClick={() => setIsExportModalOpen(true)} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-2533">
                Exportar Excel
              </Button>}
            {canCreate('insurance') && <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-2536" />} onClick={openNewForm} className="bg-white text-teal-700 hover:bg-teal-50 shadow-lg shadow-teal-900/20" data-id="element-2535">
                Nuevo Seguro
              </Button>}
          </div>
        </div>
      </div>

      {readOnly && <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg text-amber-700 dark:text-amber-300 text-sm flex items-center gap-2" data-id="element-2537">
          <LockIcon className="w-4 h-4" data-id="element-2538" />
          <span data-id="element-2539">
            Solo tienes permisos de lectura. No puedes crear, editar o eliminar
            seguros.
          </span>
        </div>}

      {/* Filters & Search */}
      <div className="flex flex-col md:flex-row gap-4 justify-between items-end" data-id="element-2540">
        <div className="w-full md:w-96 relative" data-id="element-2541">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" data-id="element-2542" />
          <Input placeholder="Buscar seguros..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700" data-id="element-2543" />
        </div>

        {/* View Mode Tabs */}
        <div className="flex items-center gap-1 p-1 bg-gray-100 dark:bg-gray-800 rounded-xl w-full md:w-auto overflow-x-auto" data-id="element-2544">
          {[{
          key: 'active',
          label: 'Activos',
          count: activeCount,
          icon: CheckCircleIcon
        }, {
          key: 'archived',
          label: 'Archivados',
          count: archivedCount,
          icon: ArchiveIcon
        }, {
          key: 'deactivated',
          label: 'Desactivados',
          count: deactivatedCount,
          icon: XCircleIcon
        }].map(tab => <button key={tab.key} onClick={() => setViewMode(tab.key as any)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${viewMode === tab.key ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`} data-id="element-2545">
              <tab.icon className="w-4 h-4" data-id="element-2546" />
              {tab.label}
              <span className="text-xs bg-gray-200 dark:bg-gray-600 px-1.5 py-0.5 rounded-full" data-id="element-2547">
                {tab.count}
              </span>
            </button>)}
        </div>
      </div>

      {/* Insurance List */}
      <Card padding="none" className="overflow-hidden border-0 shadow-lg" data-id="element-2548">
        <div className="h-1 bg-gradient-to-r from-teal-500 to-cyan-500" data-id="element-2549" />
        <div className="overflow-x-auto" data-id="element-2550">
          <table className="w-full" data-id="element-2551">
            <thead data-id="element-2552">
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50" data-id="element-2553">
                <SortableHeader label="Nombre" sortKey="name" currentDirection={isSorted('name')} onSort={requestSort} data-id="element-2554" />
                <SortableHeader label="Compañía" sortKey="company" currentDirection={isSorted('company')} onSort={requestSort} data-id="element-2555" />
                <SortableHeader label="Cobertura" sortKey="coverageType" currentDirection={isSorted('coverageType')} onSort={requestSort} data-id="element-2556" />
                <SortableHeader label="Contacto" sortKey="phone" currentDirection={isSorted('phone')} onSort={requestSort} data-id="element-2557" />
                <th className="text-right py-4 px-6 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-2558">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800" data-id="element-2559">
              {filteredInsurances.length === 0 ? <tr data-id="element-2560">
                  <td colSpan={5} className="py-16 text-center text-gray-500 dark:text-gray-400" data-id="element-2561">
                    <div className="flex flex-col items-center justify-center" data-id="element-2562">
                      <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4" data-id="element-2563">
                        <ShieldIcon className="w-8 h-8 text-gray-400" data-id="element-2564" />
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1" data-id="element-2565">
                        No se encontraron seguros
                      </h3>
                      <p className="text-sm" data-id="element-2566">
                        {searchTerm ? 'Intenta con otros términos de búsqueda' : viewMode === 'active' ? 'Comienza registrando un nuevo seguro' : 'No hay seguros en esta vista'}
                      </p>
                    </div>
                  </td>
                </tr> : paginatedInsurances.map(insurance => <tr key={insurance.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors group" data-id="element-2567">
                    <td className="py-4 px-6" data-id="element-2568">
                      <div className="flex items-center gap-3" data-id="element-2569">
                        <div className="w-8 h-8 rounded-lg bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center text-teal-600 dark:text-teal-400" data-id="element-2570">
                          <ShieldIcon className="w-4 h-4" data-id="element-2571" />
                        </div>
                        <p className="font-medium text-gray-900 dark:text-white" data-id="element-2572">
                          {insurance.name}
                        </p>
                      </div>
                    </td>
                    <td className="py-4 px-6 text-gray-900 dark:text-gray-100" data-id="element-2573">
                      {insurance.company}
                    </td>
                    <td className="py-4 px-6" data-id="element-2574">
                      <Badge variant="info" className="bg-sky-50 text-sky-700 border border-sky-100" data-id="element-2575">
                        {insurance.coverageType}
                      </Badge>
                    </td>
                    <td className="py-4 px-6" data-id="element-2576">
                      <div className="space-y-1" data-id="element-2577">
                        {insurance.phone && <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1.5" data-id="element-2578">
                            <PhoneIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-2579" />{' '}
                            {insurance.phone}
                          </p>}
                        {insurance.email && <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center gap-1.5" data-id="element-2580">
                            <MailIcon className="w-3.5 h-3.5 text-gray-400" data-id="element-2581" />{' '}
                            {insurance.email}
                          </p>}
                      </div>
                    </td>
                    <td className="py-4 px-6" data-id="element-2582">
                      <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-2583">
                        {viewMode === 'active' ? <>
                            {canEdit('insurance') && <button onClick={() => openEditForm(insurance)} className="p-2 text-gray-500 hover:text-sky-600 hover:bg-sky-50 dark:hover:bg-sky-900/20 rounded-lg transition-colors" title="Editar" data-id="element-2584">
                                <EditIcon className="w-4 h-4" data-id="element-2585" />
                              </button>}
                            {canDelete('insurance') && <>
                                <button onClick={() => openActionConfirm(insurance, 'archive')} className="p-2 text-gray-500 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors" title="Archivar" data-id="element-2586">
                                  <ArchiveIcon className="w-4 h-4" data-id="element-2587" />
                                </button>
                                <button onClick={() => openActionConfirm(insurance, 'deactivate')} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" title="Desactivar" data-id="element-2588">
                                  <PowerOffIcon className="w-4 h-4" data-id="element-2589" />
                                </button>
                              </>}
                          </> : canDelete('insurance') && <button onClick={() => restoreItem(insurance.id)} className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors" title="Restaurar" data-id="element-2590">
                              <RotateCcwIcon className="w-4 h-4" data-id="element-2591" />
                            </button>}
                      </div>
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="px-6 pb-4 border-t border-gray-100 dark:border-gray-700 pt-4" data-id="element-2592">
          <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} startIndex={startIndex} endIndex={endIndex} onPageChange={goToPage} onNext={nextPage} onPrev={prevPage} data-id="element-2593" />
        </div>
      </Card>

      {/* Form Modal */}
      <Modal isOpen={isFormOpen} onClose={() => setIsFormOpen(false)} title={selectedInsurance ? 'Editar Seguro' : 'Nuevo Seguro'} size="md" icon={<ShieldIcon className="w-5 h-5" data-id="element-2595" />} accentColor="sky" data-id="element-2594">
        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-2596">
          {/* Insurance Info */}
          <div data-id="element-2597">
            <div className="flex items-center gap-2 mb-4" data-id="element-2598">
              <span className="w-1.5 h-5 rounded-full bg-sky-500" data-id="element-2599"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-2600">
                Información del Seguro
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-2601">
              <Input label="Nombre del Seguro" value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} error={formErrors.name} placeholder="Ej: ARS Humano" data-id="element-2602" />
              <div className="grid grid-cols-2 gap-4" data-id="element-2603">
                <Input label="Compañía" value={formData.company} onChange={e => setFormData({
                ...formData,
                company: e.target.value
              })} error={formErrors.company} placeholder="Ej: Humano Seguros" data-id="element-2604" />
                <Input label="Tipo de Cobertura" value={formData.coverageType} onChange={e => setFormData({
                ...formData,
                coverageType: e.target.value
              })} error={formErrors.coverageType} placeholder="Ej: Completo, Básico" data-id="element-2605" />
              </div>
            </div>
          </div>

          {/* Contact */}
          <div data-id="element-2606">
            <div className="flex items-center gap-2 mb-4" data-id="element-2607">
              <span className="w-1.5 h-5 rounded-full bg-cyan-500" data-id="element-2608"></span>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide" data-id="element-2609">
                Contacto
              </h3>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900/30 rounded-xl p-4 space-y-4 border border-gray-100 dark:border-gray-700/50" data-id="element-2610">
              <div className="grid grid-cols-2 gap-4" data-id="element-2611">
                <Input label="Teléfono" value={formData.phone} onChange={e => setFormData({
                ...formData,
                phone: e.target.value
              })} placeholder="809-555-0000" data-id="element-2612" />
                <Input type="email" label="Correo Electrónico" value={formData.email} onChange={e => setFormData({
                ...formData,
                email: e.target.value
              })} placeholder="info@seguro.com" data-id="element-2613" />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700" data-id="element-2614">
            <Button variant="outline" type="button" onClick={() => setIsFormOpen(false)} data-id="element-2615">
              Cancelar
            </Button>
            <Button type="submit" data-id="element-2616">
              {selectedInsurance ? 'Guardar Cambios' : 'Crear Seguro'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Action Confirmation Modal */}
      <Modal isOpen={isActionConfirmOpen} onClose={() => setIsActionConfirmOpen(false)} title={actionType === 'archive' ? 'Archivar Seguro' : actionType === 'deactivate' ? 'Desactivar Seguro' : 'Eliminar Seguro'} size="sm" data-id="element-2617">
        <div className="space-y-4" data-id="element-2618">
          <p className="text-gray-600 dark:text-gray-400" data-id="element-2619">
            {actionType === 'archive' && `¿Estás seguro de que deseas archivar a "${selectedInsurance?.name}"? Podrás restaurarlo más tarde.`}
            {actionType === 'deactivate' && `¿Estás seguro de que deseas desactivar a "${selectedInsurance?.name}"? No aparecerá en las listas activas.`}
            {actionType === 'delete' && `¿Estás seguro de que deseas eliminar permanentemente a "${selectedInsurance?.name}"? Esta acción no se puede deshacer.`}
          </p>
          <div className="flex justify-end gap-3" data-id="element-2620">
            <Button variant="outline" onClick={() => setIsActionConfirmOpen(false)} data-id="element-2621">
              Cancelar
            </Button>
            <Button variant={actionType === 'delete' ? 'danger' : 'primary'} onClick={handleAction} data-id="element-2622">
              {actionType === 'archive' ? 'Archivar' : actionType === 'deactivate' ? 'Desactivar' : 'Eliminar'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Excel Export Modal */}
      <ExcelExportModal isOpen={isExportModalOpen} onClose={() => setIsExportModalOpen(false)} onExport={handleExportToExcel} title="Exportar Seguros" isExporting={isExporting} data-id="element-2623" />

      {/* PDF Export Modal */}
      <PdfExportModal isOpen={isPdfExportModalOpen} onClose={() => setIsPdfExportModalOpen(false)} onExport={handleExportToPdf} title="Exportar Seguros a PDF" isExporting={isPdfExporting} data-id="element-2624" />
    </div>;
}